'use client';

import { css } from '@repo/pandacss/styled-system/css';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { startTransition, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { MobileDialog } from '../../../OffersFilter/MobileDialog';
import { MobileFilterSortButton } from '../../../OffersFilter/MobileFilterSortButton';
import { SortByMobile } from '../../../OffersFilter/SortByMobile';
import { useFilterEvents } from '../../../OffersFilter/useFilterEvents';
import { getFiltersForPage } from '../filterConfig';
import { SelectedFilterPills } from '../SelectedFilterPills';
import { useOffersFilterStore } from '../store/useOffersFilterStore';
import { MobileSelectBonus } from './MobileSelectBonus';
import { MobileSelectLocation } from './MobileSelectLocation';
import { MobileSelectMinBalance } from './MobileSelectMinBalance';
import { MobileSelectMinDeposit } from './MobileSelectMinDeposit';
import { MobileSelectProductType } from './MobileSelectProductType';
import { MobileSelectTermLength } from './MobileSelectTermLength';

const sectionClass = css({ display: { base: 'flex', sm: 'none' } });
const scrollAreaClass = css({
  paddingTop: '4',
  paddingRight: '4',
  paddingBottom: '4',
  paddingLeft: '4',
  mb: '20',
});
const pillsWrapClass = css({ pb: '8' });

export const MobileFilters = ({
  slug,
  initialLocation,
  analyticsProps,
  onLocationReset,
  initialClientRegion,
  regionCode,
}: {
  slug: string;
  initialLocation?: StateAbbreviation;
  analyticsProps: { actionSection: string; pageIdentifier: string };
  onLocationReset?: () => void;
  initialClientRegion?: string;
  regionCode?: StateAbbreviation;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenCloseDialog = () => {
    startTransition(() => setIsDialogOpen(open => !open));
  };

  const { appliedFilters, sortOptions } = useOffersFilterStore(
    useShallow(state => ({ appliedFilters: state.appliedFilters, sortOptions: state.sortOptions })),
  );

  const { clearAllFiltersEvent, removeFilterEvent } = useFilterEvents({
    actionSection: analyticsProps.actionSection,
    pageIdentifier: analyticsProps.pageIdentifier,
    additionalAttributes: {},
  });

  const filterPillsToDisplay = useMemo(() => {
    const filters = [
      ...(appliedFilters?.selectedProductTypes || []),
      ...(appliedFilters?.selectedMinDepositRanges || []),
      ...(appliedFilters?.selectedMinBalanceRanges || []),
      ...(appliedFilters?.selectedTermRanges || []),
      ...(appliedFilters?.selectedBonusOptions || []),
    ];
    if (
      appliedFilters?.selectedLocation &&
      appliedFilters.selectedLocation !== initialClientRegion
    ) {
      filters.push(appliedFilters.selectedLocation);
    }
    return filters.length;
  }, [appliedFilters, initialClientRegion]);

  const renderFilter = (componentName: string) => {
    switch (componentName) {
      case 'ProductTypeSelect':
        return (
          <MobileSelectProductType
            key={componentName}
            regionCode={regionCode}
            {...analyticsProps}
          />
        );
      case 'LocationSelect':
        if (!initialLocation) return null;
        return <MobileSelectLocation key={componentName} {...analyticsProps} />;
      case 'TermLengthSelect':
        return <MobileSelectTermLength key={componentName} {...analyticsProps} />;
      case 'MinDepositSelect':
        return <MobileSelectMinDeposit key={componentName} {...analyticsProps} />;
      case 'MinBalanceSelect':
        return <MobileSelectMinBalance key={componentName} {...analyticsProps} />;
      case 'BonusSelect':
        return <MobileSelectBonus key={componentName} {...analyticsProps} />;
      default:
        return null;
    }
  };

  const filters = getFiltersForPage(slug, Boolean(initialLocation));

  return (
    <section className={sectionClass} aria-labelledby="mobile-page-filters">
      <h3
        id="mobile-page-filters"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
        }}
      >
        Filters
      </h3>
      <MobileFilterSortButton handleOpenCloseDialog={handleOpenCloseDialog} />
      <MobileDialog
        handleOpenCloseDialog={handleOpenCloseDialog}
        isDialogOpen={isDialogOpen}
        dialogId={`${slug}-offers-filters`}
        useStore={useOffersFilterStore}
        filterPillsToDisplay={filterPillsToDisplay}
        actionSection={analyticsProps.actionSection}
        pageIdentifier={analyticsProps.pageIdentifier}
        onLocationReset={onLocationReset}
      >
        <div className={scrollAreaClass}>
          <div className={pillsWrapClass}>
            <SelectedFilterPills
              isMobile={true}
              analyticsProps={analyticsProps}
              regionCode={regionCode}
              onClearFiltersEvent={clearAllFiltersEvent}
              onRemoveFilterEvent={removeFilterEvent}
            />
          </div>
          {sortOptions.length > 0 && (
            <SortByMobile
              sortOptions={sortOptions}
              useStore={useOffersFilterStore}
              actionSection={analyticsProps.actionSection}
              pageIdentifier={analyticsProps.pageIdentifier}
            />
          )}
          {filters.map(({ component }) => renderFilter(component))}
        </div>
      </MobileDialog>
    </section>
  );
};
